import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { DataDisplayList, DataDisplayTable } from 'components/data-display';
import { DateToLocal } from 'components/dates';
import { Money } from 'components/formatting';
import { ShowHide } from 'components/layout';
import { ListingPagination } from 'components/listing';
import { StatusBlock } from 'components/status';
import { buildFactValue, findTachoItemField } from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';
import { sumTachoGfpCost } from 'pages/employee-reviews/completed/completedReviewHelpers';
import { ClearedInfringementsNotice } from './ClearedInfringementsNotice';
import { RemovedInfringementIcon } from './RemovedInfringementIcon';
import {
    countPages,
    pageOf,
    pluralise,
    tachoSnapshotItemsPageSize
} from './tachoSnapshotHelpers';

const emptyFieldValue = "-";
const noDebriefedItemsMessage = "No infringements were debriefed on this review.";
const emergencyText = "Emergency";
const returnedDateFormat = "DD MMM YYYY";
const defaultArray = [];

// Outside a table a missing cost is left out rather than shown as a dash.
function hasGfpCost(item) {
    const value = findTachoItemField(item, "GfpCost")?.value;
    return value != null && value !== "";
}

// Infringement types are stored as sentences, so the closing full stop is dropped where the type
// leads a title line.
function buildInfringementTypeTitle(item) {
    const infringementType = buildFactValue(item, "InfringementType");

    if (typeof infringementType === "string" && infringementType.endsWith(".")) {
        return infringementType.slice(0, -1);
    }

    return infringementType;
}

function buildTextValue(value) {
    if (value == null || value === "") {
        return emptyFieldValue;
    }

    return value;
}

function buildDebriefedSummary(debriefedItems) {
    const debriefedCount = debriefedItems.length;
    return `${debriefedCount} ${pluralise(debriefedCount, "infringement", "infringements")} debriefed`;
}

function buildDebriefedListItem(item) {
    return {
        key: item.itemReference,
        title: (
            <Fragment>
                <RemovedInfringementIcon item={item} />
                <span className="bold">{buildFactValue(item, "Code")}</span>
                <span>
                    {buildInfringementTypeTitle(item)}
                    <ShowHide
                        evaluator={hasGfpCost(item)}
                        show={(
                            <Fragment> · GFP cost {buildFactValue(item, "GfpCost")}</Fragment>
                        )}
                    />
                </span>
                <ShowHide
                    evaluator={item.emergency === true}
                    show={(
                        <StatusBlock background="F0578A" large={false}>{emergencyText}</StatusBlock>
                    )}
                />
            </Fragment>
        ),
        meta: buildFactValue(item, "OccurredAt"),
        description: buildFactValue(item, "Description"),
        content: (
            <div className="spacer-top-small">
                <ShowHide
                    evaluator={item.returnedToOutstanding === true}
                    show={(
                        <p className="bold">
                            Returned to outstanding by {item.returnedToOutstandingBy} on <DateToLocal format={returnedDateFormat}>{item.returnedToOutstandingDate}</DateToLocal>
                        </p>
                    )}
                />
                <DataDisplayTable
                    displayInRows={false}
                    data={[
                        {
                            key: "reason-for-fault",
                            header: "Reason for fault",
                            value: buildTextValue(item.reasonForFault),
                            fullWidth: true
                        },
                        {
                            key: "action-taken",
                            header: "Action taken",
                            value: buildTextValue(item.actionTaken),
                            fullWidth: true
                        }
                    ]}
                />
            </div>
        )
    };
}

// Renders the Tacho Infringements topic: the caller's leading fields, a notice of any
// infringements cleared rather than debriefed (listed on request in a modal), a summary of those
// debriefed with the total GFP cost, then each debriefed infringement as it was frozen at
// completion, in the order the API returns them. An infringement since returned to outstanding keeps
// its reason and action and is marked with who returned it and when.
// Like every snapshot it renders only from what it is given, so it is safe on the anonymous
// sign-off page.
export function TachoInfringementsSnapshot(
    {
        topic = null,
        loading = false,
        leadingFields = defaultArray
    }
) {

    const [debriefedPageNumber, setDebriefedPageNumber] = useState(1);

    const items = topic?.items ?? defaultArray;
    const debriefedItems = items.filter((item) => item.cleared !== true);
    const clearedItems = items.filter((item) => item.cleared === true);

    const debriefedTotalPages = countPages(debriefedItems, tachoSnapshotItemsPageSize);
    const activeDebriefedPage = Math.min(debriefedPageNumber, debriefedTotalPages);
    const debriefedPageItems = pageOf(debriefedItems, activeDebriefedPage, tachoSnapshotItemsPageSize);

    // The total covers the debriefed infringements the line counts; cleared ones keep their cost in
    // the cleared list. A review completed before costs were captured has no total, so the line
    // keeps only the count.
    const debriefedGfpCost = sumTachoGfpCost(debriefedItems);

    return (
        <Fragment>
            <DataDisplayTable data={leadingFields} displayInRows={false} loading={loading} />
            <ClearedInfringementsNotice
                clearedItems={clearedItems}
                listedItemCount={debriefedItems.length}
                itemsClearedBefore={topic?.itemsClearedBefore ?? null}
                loading={loading}
            />
            <ShowHide
                evaluator={debriefedItems.length > 0}
                show={(
                    <p className="bp6-text-muted spacer-bottom-medium">
                        {buildDebriefedSummary(debriefedItems)}
                        <ShowHide
                            evaluator={debriefedGfpCost != null}
                            show={(
                                <Fragment> · Total GFP cost <Money>{debriefedGfpCost}</Money></Fragment>
                            )}
                        />
                    </p>
                )}
            />
            <div className="completed-review-topic-items">
                <ShowHide
                    evaluator={debriefedItems.length > 0}
                    show={(
                        <Fragment>
                            <DataDisplayList
                                id="tacho-infringement-items"
                                items={debriefedPageItems.map(buildDebriefedListItem)}
                                loading={loading}
                            />
                            <ShowHide
                                evaluator={debriefedTotalPages > 1}
                                show={(
                                    <ListingPagination
                                        currentPage={activeDebriefedPage}
                                        totalPages={debriefedTotalPages}
                                        loadingData={loading}
                                        onPageChange={setDebriefedPageNumber}
                                    />
                                )}
                            />
                        </Fragment>
                    )}
                    hide={(
                        <p>{noDebriefedItemsMessage}</p>
                    )}
                />
            </div>
        </Fragment>
    );
}

TachoInfringementsSnapshot.propTypes = {
    topic: PropTypes.object,
    loading: PropTypes.bool,
    leadingFields: PropTypes.array
};
